import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import infraestruturaRowsService from '../services/infraestruturaRowsService';
import {
  INFRA_FILTER_FIELDS,
  applyInfraFilters,
  buildFilterLabel,
  normalizeFilterValue,
  sameFilter,
  serializeHistoryFilters,
} from '../utils/infraAnalytics';

const InfraestruturaDataContext = createContext(null);
const POLL_INTERVAL_MS = 30000;
const POLL_TIMEOUT_MS = 120000;

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const buildDefaultRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { presetId: 'mtd', start: toISO(start), end: toISO(today) };
};

const buildRangeFromMonth = (rawMonth) => {
  const match = String(rawMonth ?? '').trim().match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const [, yearText, monthText] = match;
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }

  const startDate = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  const today = new Date();
  const isCurrentMonth = year === today.getFullYear() && monthIndex === today.getMonth();
  const endDate = isCurrentMonth ? today : monthEnd;

  return {
    presetId: `month:${yearText}-${monthText}`,
    start: toISO(startDate),
    end: toISO(endDate),
  };
};

const buildEmptyEvolution = (overrides = {}) => ({
  months: [],
  scope: 'general',
  requested_scope: 'general',
  cache_state: 'idle',
  last_updated: null,
  filter_hash: null,
  ...overrides,
});

const extractRangeMonth = (range) => {
  const start = String(range?.start || '').trim();
  const end = String(range?.end || '').trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return null;
  }

  const startMonth = start.slice(0, 7);
  const endMonth = end.slice(0, 7);

  return startMonth === endMonth ? startMonth : null;
};

export const InfraestruturaDataProvider = ({ children }) => {
  const [range, setRange] = useState(buildDefaultRange);
  const [rowsRaw, setRowsRaw] = useState([]);
  const [evolutionGeneral, setEvolutionGeneral] = useState(buildEmptyEvolution());
  const [evolutionFiltered, setEvolutionFiltered] = useState(buildEmptyEvolution({ requested_scope: 'filtered' }));
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [cacheState, setCacheState] = useState('idle');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const requestAbortRef = useRef(null);
  const pollRef = useRef(null);
  const pollingStartedAtRef = useRef(null);
  const requestInFlightRef = useRef(false);

  const evolutionAbortRef = useRef(null);
  const evolutionPollRef = useRef(null);
  const evolutionPollingStartedAtRef = useRef(null);
  const evolutionRequestInFlightRef = useRef(false);

  const rowsFiltered = useMemo(
    () => applyInfraFilters(rowsRaw, filters),
    [filters, rowsRaw],
  );

  const historyFilters = useMemo(
    () => serializeHistoryFilters(filters),
    [filters],
  );

  const activeRangeMonth = useMemo(
    () => extractRangeMonth(range),
    [range],
  );

  const hasHistoryFilters = historyFilters.length > 0;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollingStartedAtRef.current = null;
  }, []);

  const stopEvolutionPolling = useCallback(() => {
    if (evolutionPollRef.current) {
      clearInterval(evolutionPollRef.current);
      evolutionPollRef.current = null;
    }
    evolutionPollingStartedAtRef.current = null;
  }, []);

  const fetchRows = useCallback(async ({ fromPolling = false, retry = false } = {}) => {
    if (fromPolling && requestInFlightRef.current) {
      return;
    }

    if (!fromPolling) {
      stopPolling();
      setErrorMessage('');
      setProgress(null);
    }

    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
    }

    const controller = new AbortController();
    requestAbortRef.current = controller;
    const signal = controller.signal;

    if (!fromPolling) {
      setLoading(true);
    }

    requestInFlightRef.current = true;

    try {
      const response = await infraestruturaRowsService.buscarRows(
        { data_inicio: range.start, data_fim: range.end, retry },
        signal,
      );

      if (signal.aborted) {
        return;
      }

      if (response.status === 'processing') {
        setProcessing(true);
        setCacheState('processing');
        setProgress(response.progress || null);
        setEvolutionGeneral(buildEmptyEvolution(response.evolution));

        if (!pollingStartedAtRef.current) {
          pollingStartedAtRef.current = Date.now();
        }

        if (Date.now() - pollingStartedAtRef.current >= POLL_TIMEOUT_MS) {
          stopPolling();
          setProcessing(false);
          setCacheState('timeout');
          setProgress(null);
          setRowsRaw([]);
          setEvolutionGeneral(buildEmptyEvolution({ cache_state: 'timeout' }));
          setErrorMessage('Dados temporariamente indisponíveis para este período.');
          return;
        }

        if (!pollRef.current) {
          pollRef.current = setInterval(() => {
            fetchRows({ fromPolling: true });
          }, POLL_INTERVAL_MS);
        }

        return;
      }

      stopPolling();
      setProcessing(false);
      setCacheState('ready');
      setProgress(null);
      setRowsRaw(Array.isArray(response.rows) ? response.rows : []);
      setEvolutionGeneral(buildEmptyEvolution(response.evolution));
      setLastUpdated(new Date());
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }

      stopPolling();
      setProcessing(false);
      setCacheState('error');
      setRowsRaw([]);
      setEvolutionGeneral(buildEmptyEvolution({ cache_state: 'error' }));
      setErrorMessage(error?.response?.data?.code === 'IXC_UNAVAILABLE'
        ? 'A fonte IXC está indisponível no momento. Tente novamente.'
        : 'Não foi possível carregar os dados agora.');
    } finally {
      requestInFlightRef.current = false;

      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [range.end, range.start, stopPolling]);

  const fetchFilteredEvolution = useCallback(async ({ fromPolling = false, retry = false } = {}) => {
    if (!hasHistoryFilters) {
      stopEvolutionPolling();
      if (evolutionAbortRef.current) {
        evolutionAbortRef.current.abort();
      }
      setEvolutionFiltered(buildEmptyEvolution({ requested_scope: 'filtered' }));
      return;
    }

    if (fromPolling && evolutionRequestInFlightRef.current) {
      return;
    }

    if (!fromPolling) {
      stopEvolutionPolling();
      setEvolutionFiltered((previous) => ({
        ...buildEmptyEvolution({
          ...evolutionGeneral,
          scope: evolutionGeneral.scope || 'general',
          requested_scope: 'filtered',
          cache_state: 'warming',
          filter_hash: previous.filter_hash,
        }),
      }));
    }

    if (evolutionAbortRef.current) {
      evolutionAbortRef.current.abort();
    }

    const controller = new AbortController();
    evolutionAbortRef.current = controller;
    const signal = controller.signal;
    evolutionRequestInFlightRef.current = true;

    try {
      const response = await infraestruturaRowsService.buscarEvolution(
        { data_inicio: range.start, data_fim: range.end, filters: historyFilters, retry },
        signal,
      );

      if (signal.aborted) {
        return;
      }

      const normalizedEvolution = buildEmptyEvolution({
        ...response.evolution,
        requested_scope: response.evolution?.requested_scope || 'filtered',
      });

      setEvolutionFiltered(normalizedEvolution);

      if (response.status === 'processing') {
        if (!evolutionPollingStartedAtRef.current) {
          evolutionPollingStartedAtRef.current = Date.now();
        }

        if (Date.now() - evolutionPollingStartedAtRef.current >= POLL_TIMEOUT_MS) {
          stopEvolutionPolling();
          setEvolutionFiltered((previous) => ({
            ...previous,
            cache_state: 'timeout',
          }));
          return;
        }

        if (!evolutionPollRef.current) {
          evolutionPollRef.current = setInterval(() => {
            fetchFilteredEvolution({ fromPolling: true });
          }, POLL_INTERVAL_MS);
        }

        return;
      }

      stopEvolutionPolling();
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }

      stopEvolutionPolling();
      setEvolutionFiltered(buildEmptyEvolution({
        ...evolutionGeneral,
        requested_scope: 'filtered',
        scope: 'general',
        cache_state: 'error',
      }));
    } finally {
      evolutionRequestInFlightRef.current = false;
    }
  }, [evolutionGeneral, hasHistoryFilters, historyFilters, range.end, range.start, stopEvolutionPolling]);

  useEffect(() => {
    fetchRows({ fromPolling: false });
  }, [fetchRows, range.end, range.start]);

  useEffect(() => {
    fetchFilteredEvolution({ fromPolling: false });
  }, [fetchFilteredEvolution, range.end, range.start, historyFilters]);

  useEffect(() => () => {
    stopPolling();
    stopEvolutionPolling();
    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
    }
    if (evolutionAbortRef.current) {
      evolutionAbortRef.current.abort();
    }
  }, [stopEvolutionPolling, stopPolling]);

  const addFilter = useCallback((filter) => {
    if (!filter?.field) return;
    const next = {
      field: filter.field,
      value: normalizeFilterValue(filter.value),
      label: filter.label || buildFilterLabel(filter.field, normalizeFilterValue(filter.value)),
      ...(filter.eventType ? { eventType: filter.eventType } : {}),
    };

    setFilters((previous) => (
      previous.some((item) => sameFilter(item, next))
        ? previous
        : [...previous, next]
    ));
  }, []);

  const removeFilter = useCallback((filter) => {
    if (!filter?.field) return;
    setFilters((previous) => previous.filter((item) => !sameFilter(item, filter)));
  }, []);

  const toggleFilter = useCallback((filter) => {
    if (!filter?.field) return;
    const next = {
      field: filter.field,
      value: normalizeFilterValue(filter.value),
      label: filter.label || buildFilterLabel(filter.field, normalizeFilterValue(filter.value)),
      ...(filter.eventType ? { eventType: filter.eventType } : {}),
    };

    setFilters((previous) => (
      previous.some((item) => sameFilter(item, next))
        ? previous.filter((item) => !sameFilter(item, next))
        : [...previous, next]
    ));
  }, []);

  const clearFilters = useCallback(() => setFilters([]), []);

  const retry = useCallback(() => {
    fetchRows({ retry: true });
    if (hasHistoryFilters) {
      fetchFilteredEvolution({ retry: true });
    }
  }, [fetchFilteredEvolution, fetchRows, hasHistoryFilters]);

  const selectHistoricalMonth = useCallback((rawMonth) => {
    const nextRange = buildRangeFromMonth(rawMonth);
    if (!nextRange) {
      return;
    }

    setFilters((previous) => previous.filter(
      (item) => item?.field !== 'ano_mes_abertura' && item?.field !== 'ano_mes_fechamento' && item?.field !== 'ano_mes',
    ));
    setRange(nextRange);
  }, []);

  const evolutionActive = useMemo(() => {
    if (!hasHistoryFilters) {
      return evolutionGeneral;
    }

    return buildEmptyEvolution({
      ...evolutionFiltered,
      months: Array.isArray(evolutionFiltered.months) ? evolutionFiltered.months : evolutionGeneral.months,
      last_updated: evolutionFiltered.last_updated || evolutionGeneral.last_updated,
      filter_hash: evolutionFiltered.filter_hash,
      requested_scope: evolutionFiltered.requested_scope || 'filtered',
      scope: evolutionFiltered.scope || 'general',
      cache_state: evolutionFiltered.cache_state || 'warming',
    });
  }, [evolutionFiltered, evolutionGeneral, hasHistoryFilters]);

  const value = useMemo(() => ({
    range,
    setRange,
    rowsRaw,
    rowsFiltered,
    evolutionMonths: evolutionActive.months,
    evolutionCacheState: evolutionActive.cache_state,
    evolutionLastUpdated: evolutionActive.last_updated,
    evolutionScope: evolutionActive.scope,
    evolutionRequestedScope: evolutionActive.requested_scope,
    evolutionFilterHash: evolutionActive.filter_hash,
    evolutionGeneralMonths: evolutionGeneral.months,
    evolutionFilteredMonths: evolutionFiltered.months,
    activeRangeMonth,
    historyFilters,
    hasHistoryFilters,
    filters,
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
    selectHistoricalMonth,
    loading,
    processing,
    progress,
    cacheState,
    lastUpdated,
    errorMessage,
    retry,
    availableFilterFields: INFRA_FILTER_FIELDS,
  }), [
    addFilter,
    cacheState,
    clearFilters,
    errorMessage,
    evolutionActive.cache_state,
    evolutionActive.filter_hash,
    evolutionActive.last_updated,
    evolutionActive.months,
    evolutionActive.requested_scope,
    evolutionActive.scope,
    evolutionFiltered.months,
    evolutionGeneral.months,
    activeRangeMonth,
    filters,
    hasHistoryFilters,
    historyFilters,
    lastUpdated,
    loading,
    progress,
    processing,
    range,
    removeFilter,
    retry,
    rowsFiltered,
    rowsRaw,
    selectHistoricalMonth,
    setRange,
    toggleFilter,
  ]);

  return (
    <InfraestruturaDataContext.Provider value={value}>
      {children}
    </InfraestruturaDataContext.Provider>
  );
};

export const useInfraestruturaData = () => {
  const context = useContext(InfraestruturaDataContext);
  if (!context) {
    throw new Error('useInfraestruturaData must be used within InfraestruturaDataProvider');
  }

  return context;
};

export default InfraestruturaDataContext;
